import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ChatCircleDots, Plus, MagnifyingGlass, PaperPlaneRight, Image as ImageIcon, X, Trash, PencilSimple,
  Copy, Check, Users as UsersIcon, User, Hash, ArrowLeft, DotsThreeVertical, UserPlus,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useOutletContext } from 'react-router-dom';

const FILE_INPUT_ID = 'chat-file-input';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer ' + d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
};

const roleBadgeColor = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'admin': return 'bg-rose-100 text-rose-700';
    case 'supervisor': return 'bg-amber-100 text-amber-700';
    case 'gerente': return 'bg-purple-100 text-purple-700';
    case 'vendor':
    case 'vendedor': return 'bg-blue-100 text-blue-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// ────────────────────────── New Chat Modal ──────────────────────────
const NewChatModal = ({ open, onClose, onCreated, currentUser, allowChannel }) => {
  const [mode, setMode] = useState('dm');     // 'dm' | 'channel'
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [channelName, setChannelName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/chat/users').then(({ data }) => setUsers(data || []));
    setMode('dm'); setSelected([]); setSearch(''); setChannelName('');
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return users.filter(u => !s || u.name.toLowerCase().includes(s));
  }, [users, search]);

  const submit = async () => {
    if (mode === 'dm' && selected.length !== 1) return toast.error('Selecciona 1 usuario');
    if (mode === 'channel' && !channelName.trim()) return toast.error('Nombre del canal requerido');
    if (mode === 'channel' && selected.length === 0) return toast.error('Agrega al menos 1 miembro');
    setLoading(true);
    try {
      const { data } = await api.post('/chat/chats', {
        type: mode,
        name: mode === 'channel' ? channelName.trim() : null,
        members: selected,
      });
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear');
    } finally { setLoading(false); }
  };

  const toggle = (id) => {
    if (mode === 'dm') setSelected([id]);
    else setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[var(--surface)] border-[var(--border)]" data-testid="new-chat-modal">
        <DialogHeader>
          <DialogTitle className="text-[var(--text)]">Nuevo chat</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 mb-3">
          <button onClick={() => { setMode('dm'); setSelected([]); }} className={`flex-1 py-2 rounded text-xs font-bold ${mode === 'dm' ? 'bg-blue-600 text-white' : 'bg-[var(--surface-muted)] text-[var(--text-muted)]'}`} data-testid="mode-dm">
            <User size={12} className="inline mr-1" /> Privado
          </button>
          {allowChannel && (
            <button onClick={() => setMode('channel')} className={`flex-1 py-2 rounded text-xs font-bold ${mode === 'channel' ? 'bg-blue-600 text-white' : 'bg-[var(--surface-muted)] text-[var(--text-muted)]'}`} data-testid="mode-channel">
              <Hash size={12} className="inline mr-1" /> Canal de equipo
            </button>
          )}
        </div>
        {mode === 'channel' && (
          <Input value={channelName} onChange={(e) => setChannelName(e.target.value)} placeholder="Nombre del canal (ej. Ventas, Logística…)" className="mb-2" data-testid="channel-name-input" />
        )}
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar usuario…" className="mb-2" />
        <div className="max-h-72 overflow-y-auto border border-[var(--border)] rounded">
          {filtered.length === 0 ? <p className="p-4 text-center text-xs text-[var(--text-muted)]">Sin usuarios</p> :
            filtered.map(u => (
              <button key={u.id} onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-2 p-2 text-left hover:bg-[var(--surface-muted)] ${selected.includes(u.id) ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
                data-testid={`user-${u.id}`}>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold">{u.name.slice(0, 1).toUpperCase()}</div>
                <div className="flex-1">
                  <div className="text-sm text-[var(--text)] font-bold">{u.name}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${roleBadgeColor(u.role)}`}>{u.role}</span>
                </div>
                {selected.includes(u.id) && <Check size={16} className="text-blue-600" weight="bold" />}
              </button>
            ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-chat-btn">
            {loading ? 'Creando…' : 'Crear chat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────── Message Row ──────────────────────────
const MessageRow = ({ msg, mine, onEdit, onDelete, onCopy }) => {
  const [menu, setMenu] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  if (msg.deleted_at) {
    return (
      <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
        <div className="max-w-[75%] px-3 py-2 rounded-lg bg-[var(--surface-muted)] text-[var(--text-muted)] text-xs italic">
          🗑 Mensaje eliminado
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'} gap-1 group`}>
      <div className={`relative max-w-[75%] px-3 py-2 rounded-2xl shadow-sm ${mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-bl-sm'}`}>
        {!mine && <div className="text-[10px] font-bold mb-1 opacity-70">{msg.sender_name}</div>}
        {msg.attachments?.map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="block mb-1">
            <img src={a.url} alt={a.name || 'imagen'} className="rounded-lg max-h-60 object-cover" />
          </a>
        ))}
        {msg.content && <div className="text-sm whitespace-pre-wrap break-words">{msg.content}</div>}
        <div className={`text-[9px] mt-1 ${mine ? 'text-blue-100' : 'text-[var(--text-muted)]'} flex items-center gap-1`}>
          {fmtTime(msg.created_at)}
          {msg.edited_at && <span className="italic">· editado</span>}
        </div>
        <div ref={ref} className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition">
          <button onClick={() => setMenu(m => !m)} className="bg-white shadow rounded-full p-1 border border-gray-200" data-testid={`msg-menu-${msg.id}`}>
            <DotsThreeVertical size={12} className="text-gray-600" />
          </button>
          {menu && (
            <div className="absolute right-0 top-7 bg-white dark:bg-gray-800 shadow-xl rounded-md border border-[var(--border)] py-1 min-w-[140px] z-50">
              <button onClick={() => { onCopy(msg); setMenu(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--surface-muted)] flex items-center gap-2 text-[var(--text)]" data-testid={`copy-${msg.id}`}>
                <Copy size={12} /> Copiar
              </button>
              {mine && (
                <>
                  <button onClick={() => { onEdit(msg); setMenu(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--surface-muted)] flex items-center gap-2 text-[var(--text)]" data-testid={`edit-${msg.id}`}>
                    <PencilSimple size={12} /> Editar
                  </button>
                  <button onClick={() => { onDelete(msg); setMenu(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 text-red-600 flex items-center gap-2" data-testid={`delete-${msg.id}`}>
                    <Trash size={12} /> Eliminar
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────── Main Page ──────────────────────────
export default function Chat() {
  const { user } = useOutletContext() || {};
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [input, setInput] = useState('');
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchChat, setSearchChat] = useState('');
  const messagesEndRef = useRef(null);
  const lastMsgTsRef = useRef(null);
  const allowChannel = ['admin', 'supervisor', 'gerente'].includes((user?.role || '').toLowerCase());

  const active = useMemo(() => chats.find(c => c.id === activeId), [chats, activeId]);

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/chats');
      setChats(data || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadMessages = useCallback(async (chatId, append = false) => {
    if (!chatId) return;
    try {
      const params = {};
      if (append && lastMsgTsRef.current) params.after = lastMsgTsRef.current;
      const { data } = await api.get(`/chat/chats/${chatId}/messages`, { params });
      if (data && data.length) {
        if (append) {
          setMessages(prev => [...prev, ...data]);
        } else {
          setMessages(data);
        }
        lastMsgTsRef.current = data[data.length - 1].created_at;
      } else if (!append) {
        setMessages([]);
        lastMsgTsRef.current = null;
      }
    } catch (e) { /* silent */ }
  }, []);

  // Initial load
  useEffect(() => { loadChats(); }, [loadChats]);

  // Pick chat from URL or default to first
  useEffect(() => {
    const cid = searchParams.get('chat');
    if (cid && chats.find(c => c.id === cid)) setActiveId(cid);
    else if (!activeId && chats.length > 0) setActiveId(chats[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats]);

  // Load messages when chat changes
  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    lastMsgTsRef.current = null;
    loadMessages(activeId, false);
    api.post(`/chat/chats/${activeId}/read`).then(loadChats).catch(() => {});
  }, [activeId, loadMessages, loadChats]);

  // Polling every 5s for new messages and chat list updates
  useEffect(() => {
    const intv = setInterval(() => {
      if (activeId) loadMessages(activeId, true);
      loadChats();
    }, 5000);
    return () => clearInterval(intv);
  }, [activeId, loadMessages, loadChats]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async () => {
    if (!input.trim() && pendingAttachments.length === 0) return;
    try {
      if (editing) {
        await api.put(`/chat/messages/${editing.id}`, { content: input });
        setEditing(null);
      } else {
        await api.post(`/chat/chats/${activeId}/messages`, {
          content: input,
          attachments: pendingAttachments,
        });
      }
      setInput('');
      setPendingAttachments([]);
      loadMessages(activeId, true);
      loadChats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar');
    }
  };

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/chat/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPendingAttachments(p => [...p, data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo subir');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteMsg = async (msg) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    try {
      await api.delete(`/chat/messages/${msg.id}`);
      setMessages(ms => ms.map(m => m.id === msg.id ? { ...m, deleted_at: new Date().toISOString(), content: '', attachments: [] } : m));
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  const copyMsg = (msg) => {
    navigator.clipboard.writeText(msg.content || '');
    toast.success('Copiado');
  };

  const deleteChat = async () => {
    if (!active || !window.confirm(`¿Eliminar chat "${active.display_name}"?`)) return;
    try {
      await api.delete(`/chat/chats/${active.id}`);
      toast.success('Chat eliminado');
      setActiveId(null);
      loadChats();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  const filteredChats = useMemo(() => {
    const s = searchChat.toLowerCase().trim();
    if (!s) return chats;
    return chats.filter(c => (c.display_name || '').toLowerCase().includes(s));
  }, [chats, searchChat]);

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="chat-page">
      {/* Sidebar list */}
      <Card className={`w-full md:w-80 border-r border-[var(--border)] rounded-none flex flex-col bg-[var(--surface)] ${activeId ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="text-sm font-black text-[var(--text)] flex items-center gap-2">
            <ChatCircleDots size={18} weight="duotone" className="text-blue-600" /> Chat interno
          </h2>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setNewOpen(true)} data-testid="new-chat-btn">
            <Plus size={14} /> Nuevo
          </Button>
        </div>
        <div className="p-2 border-b border-[var(--border)]">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={searchChat} onChange={(e) => setSearchChat(e.target.value)} placeholder="Buscar chat…" className="pl-7 h-9 text-xs" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredChats.length === 0 ? (
            <div className="p-6 text-center text-xs text-[var(--text-muted)]">
              <ChatCircleDots size={32} className="mx-auto mb-2 opacity-40" />
              No tienes chats. Crea el primero.
            </div>
          ) : filteredChats.map(c => (
            <button
              key={c.id}
              onClick={() => { setActiveId(c.id); setSearchParams({ chat: c.id }); }}
              className={`w-full flex items-start gap-2 p-3 border-b border-[var(--border)] text-left hover:bg-[var(--surface-muted)] ${activeId === c.id ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
              data-testid={`chat-${c.id}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${c.type === 'channel' ? 'bg-gradient-to-br from-emerald-500 to-emerald-700' : 'bg-gradient-to-br from-blue-500 to-blue-700'}`}>
                {c.type === 'channel' ? <Hash size={18} weight="bold" /> : (c.display_name?.[0] || '?').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <div className="text-sm font-bold text-[var(--text)] truncate">{c.display_name}</div>
                  {c.unread > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">{c.unread}</span>}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] truncate">
                  {c.last_message_preview || (c.type === 'channel' ? `${c.member_count} miembros` : '—')}
                </div>
                {c.type === 'dm' && c.other_user_role && (
                  <span className={`text-[9px] px-1 py-0 rounded mt-0.5 inline-block ${roleBadgeColor(c.other_user_role)}`}>{c.other_user_role}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Messages panel */}
      <div className={`flex-1 flex flex-col bg-[var(--bg)] ${activeId ? 'flex' : 'hidden md:flex'}`}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-[var(--text-muted)]">
            <ChatCircleDots size={48} className="opacity-40 mb-2" />
            <p className="text-sm">Selecciona un chat para empezar</p>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-[var(--border)] bg-[var(--surface)] flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Button size="sm" variant="ghost" className="md:hidden" onClick={() => setActiveId(null)} data-testid="back-to-list">
                  <ArrowLeft size={16} />
                </Button>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${active.type === 'channel' ? 'bg-gradient-to-br from-emerald-500 to-emerald-700' : 'bg-gradient-to-br from-blue-500 to-blue-700'}`}>
                  {active.type === 'channel' ? <Hash size={16} weight="bold" /> : (active.display_name?.[0] || '?').toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-[var(--text)] truncate">{active.display_name}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {active.type === 'channel' ? <><UsersIcon size={9} className="inline" /> {active.member_count} miembros</> : active.other_user_role}
                  </div>
                </div>
              </div>
              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={deleteChat} data-testid="delete-chat-btn">
                <Trash size={14} />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {messages.length === 0 ? (
                <p className="text-center text-xs text-[var(--text-muted)] py-12">Sin mensajes aún. ¡Manda el primero!</p>
              ) : messages.map(m => (
                <MessageRow
                  key={m.id}
                  msg={m}
                  mine={m.sender_id === user?.id}
                  onEdit={(msg) => { setEditing(msg); setInput(msg.content || ''); }}
                  onDelete={deleteMsg}
                  onCopy={copyMsg}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {pendingAttachments.length > 0 && (
              <div className="p-2 border-t border-[var(--border)] bg-[var(--surface)] flex gap-2 flex-wrap">
                {pendingAttachments.map((a, i) => (
                  <div key={i} className="relative">
                    <img src={a.url} alt="" className="w-16 h-16 object-cover rounded" />
                    <button onClick={() => setPendingAttachments(p => p.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {editing && (
              <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-300 flex items-center justify-between">
                <span className="text-xs text-amber-700 dark:text-amber-300">✏️ Editando mensaje</span>
                <button onClick={() => { setEditing(null); setInput(''); }} className="text-xs text-amber-700 underline">Cancelar</button>
              </div>
            )}

            <div className="p-2 pb-24 md:pb-2 border-t border-[var(--border)] bg-[var(--surface)] flex items-end gap-2">
              <input id={FILE_INPUT_ID} type="file" accept="image/*" onChange={handleAttach} className="hidden" data-testid="chat-file-input" />
              <Button size="sm" variant="ghost" disabled={uploading || !!editing} onClick={() => document.getElementById(FILE_INPUT_ID).click()} className="text-[var(--text-muted)]" data-testid="attach-image-btn">
                {uploading ? '…' : <ImageIcon size={18} />}
              </Button>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={editing ? 'Editar mensaje…' : 'Escribe un mensaje… (Enter para enviar)'}
                rows={1}
                className="flex-1 resize-none min-h-[40px] max-h-32 text-sm"
                data-testid="message-input"
              />
              <Button size="sm" onClick={send} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-btn">
                <PaperPlaneRight size={16} weight="fill" />
              </Button>
            </div>
          </>
        )}
      </div>

      <NewChatModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={(c) => { setActiveId(c.id); loadChats(); }} currentUser={user} allowChannel={allowChannel} />
    </div>
  );
}
